import { Result } from './FormModel';

export const convertToNumber =
    (message: string) =>
    (value: string): Result<number> => {
        const valueNumber = parseFloat(value);
        return isNaN(valueNumber) ? Result.createError(message) : Result.createOk(valueNumber);
    };

export const convertAndValidToFloat =
    (message: string) =>
    (value: string): Result<string> => {
        if (value.trim() === '') {
            return Result.createError(message);
        }

        if (
            value.split('.').length > 2 ||
            value.split(',').length > 2 ||
            (value.includes('.') && value.includes(','))
        ) {
            return Result.createError(message);
        }

        const valueWithDot = value.replace(',', '.');
        // Number in this context is correct method
        // eslint-disable-next-line no-restricted-globals
        const valueNumber = Number(valueWithDot);

        return isNaN(valueNumber) ? Result.createError(message) : Result.createOk(valueNumber.toString());
    };

export const validateNotEmpty =
    (message: string) =>
    (value: string | undefined): Result<string> =>
        value === '' || value === undefined ? Result.createError(message) : Result.createOk(value);

export const validateMaxLength =
    (maxLength: number, message?: string) =>
    (value: string): Result<string> => {
        if (value.length <= maxLength) {
            return Result.createOk(value);
        }

        const excess = value.length - maxLength;
        const excessLabel = excess === 1 ? 'character' : 'characters';

        return Result.createError(
            message ?? `${excess} ${excessLabel} over the ${maxLength} character limit`
        );
    };

export const validateRange =
    (from: number, to: number, message: string) =>
    (value: number): Result<number> =>
        from <= value && value <= to ? Result.createOk(value) : Result.createError(message);

export const validateHexColor =
    (message: string) =>
    (value: string): Result<string> => {
        const length = value.length;
        if (value === '') {
            return Result.createOk(value);
        }
        if (value.startsWith('#') && (length === 7 || length === 4 || length === 9)) {
            if (!isNaN(parseInt(value.slice(1), 16))) {
                return Result.createOk(value);
            }
            return Result.createError(message);
        }
        return Result.createError(message);
    };

const isValidHttpUrl = (value: string): boolean => {
    let url;

    try {
        url = new URL(value);
    } catch {
        return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:';
};

export const validateExpectLink =
    (message: string) =>
    (value: string): Result<string> => {
        if (value === '') {
            return Result.createOk(value);
        }

        if (!isValidHttpUrl(value)) {
            return Result.createError(message);
        }

        return Result.createOk(value);
    };

export const validateSlug =
    (message: string) =>
    (value: string): Result<string> => {
        if (value.match(/^[a-z0-9-]+$/g) !== null) {
            return Result.createOk(value);
        }

        return Result.createError(message);
    };

export const convertToNumberArray =
    (message: string) =>
    (value: string): Result<Array<number>> => {
        const stringNumbersList = value.split(',');
        const numbersList = stringNumbersList.map((elem) => parseInt(elem));
        const isWrongNumber = numbersList.some((elem) => isNaN(elem));

        return isWrongNumber ? Result.createError(message) : Result.createOk(numbersList);
    };

export const validateEmailRaw = (value: string): boolean => {
    const EMAIL_REGEXP = /^([\w\-+]+(?:\.[\w\-+]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/;

    if (!EMAIL_REGEXP.test(value)) {
        return false;
    }

    return true;
};

export const validateEmail =
    (message: string) =>
    (value: string): Result<string> => {
        const isOk = validateEmailRaw(value);

        if (!isOk) {
            return Result.createError(message);
        }

        return Result.createOk(value);
    };

export const validatePhoneNumber =
    (message: string) =>
    (value: string): Result<string> => {
        const REGEXP = /^(\s*[0-9]\s*){7,15}$/;
        if (!REGEXP.test(value)) {
            return Result.createError(message);
        }
        return Result.createOk(value);
    };

export const validateCommaSeparatedFloats =
    (message: string) =>
    (value: string): Result<string> => {
        const parts = value.split(',');
        for (const part of parts) {
            const trimmed = part.trim();
            if (trimmed === '') continue;

            const valueNumber = parseFloat(trimmed);
            if (isNaN(valueNumber)) {
                return Result.createError(message);
            }
        }

        return Result.createOk(value);
    };
